import { detectIOS, detectYoutubeUrls } from "./detectParams";

class VideoPreloader {
    constructor() {
        this.cache = new Map();
        this.preloadQueue = [];
        this.isProcessing = false;
    }

    async preloadVideo(url, priority = "low") {
        // Skip preloading for YouTube videos
        if (detectYoutubeUrls(url)) {
            console.debug("Skipping preload for YouTube video:", url);
            return Promise.resolve({ url, type: "youtube", duration: 0 });
        }

        // Skip preloading for invalid URLs
        if (!url || typeof url !== 'string' || url.trim() === '') {
            console.warn("Invalid URL for preloading:", url);
            return Promise.reject(new Error("Invalid URL"));
        }

        if (this.cache.has(url)) {
            return this.cache.get(url);
        }

        const preloadItem = { url, priority, promise: null };
        if (priority === "high") {
            this.preloadQueue.unshift(preloadItem);
        } else {
            this.preloadQueue.push(preloadItem);
        }

        preloadItem.promise = this.createPreloadPromise(url);
        this.cache.set(url, preloadItem.promise);

        if (!this.isProcessing) {
            this.processQueue();
        }

        return preloadItem.promise;
    }

    createPreloadPromise(url) {
        return new Promise((resolve, reject) => {
            try {
                const video = document.createElement("video");
                video.preload = "metadata";
                video.muted = true;
                video.playsInline = true;

                if (detectIOS()) {
                    video.setAttribute("webkit-playsinline", "true");
                    video.setAttribute("x5-playsinline", "true");
                    video.setAttribute("x5-video-player-type", "h5");
                    video.setAttribute("x5-video-player-fullscreen", "false");
                }

                const timeout = setTimeout(() => {
                    console.warn("Preload timeout for:", url);
                    reject(new Error("Preload timeout"));
                }, 15000); // Increased timeout to 15 seconds

                video.onloadedmetadata = () => {
                    clearTimeout(timeout);
                    console.debug("Preload successful for:", url);
                    resolve({
                        url,
                        duration: video.duration,
                        readyState: video.readyState,
                        type: "video"
                    });
                };

                video.onerror = (error) => {
                    clearTimeout(timeout);
                    console.warn("Preload failed for:", url, error);
                    reject(new Error(`Preload failed: ${error.message || 'Unknown error'}`));
                };

                video.onabort = () => {
                    clearTimeout(timeout);
                    console.warn("Preload aborted for:", url);
                    reject(new Error("Preload aborted"));
                };

                video.src = url;
            } catch (error) {
                console.error("Error creating preload promise for:", url, error);
                reject(new Error(`Preload setup failed: ${error.message}`));
            }
        });
    }

    async processQueue() {
        this.isProcessing = true;

        while (this.preloadQueue.length > 0) {
            const item = this.preloadQueue.shift();
            try {
                await item.promise;
            } catch (error) {
                console.warn("Preload failed for:", item.url, error.message);
                // Don't throw here, just log the error and continue
            }

            // Small delay to prevent blocking
            await new Promise((resolve) => setTimeout(resolve, 100));
        }

        this.isProcessing = false;
    }

    clearCache() {
        this.cache.clear();
        this.preloadQueue = [];
    }

    // Get cache statistics
    getCacheStats() {
        return {
            cacheSize: this.cache.size,
            queueSize: this.preloadQueue.length,
            isProcessing: this.isProcessing
        };
    }
}

export const videoPreloader = new VideoPreloader();